import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

/**
 * Custom hook for authentication state
 * Provides auth status, user data, and auth methods
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    const authenticated = authService.isAuthenticated();
    const currentUser = authService.getCurrentUser();
    
    setIsAuthenticated(authenticated);
    setUser(currentUser);
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    checkAuth();
    return response;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
    checkAuth
  };
};

/**
 * Custom hook for API calls with loading and error states
 */
export const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const execute = useCallback(async (apiCall) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await apiCall();
      return { success: true, data: result };
    } catch (err) {
      const errorMessage = err.message || 'An error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const showSuccess = useCallback((message) => {
    setSuccess(message);
    setError('');
  }, []);

  const showError = useCallback((message) => {
    setError(message);
    setSuccess('');
  }, []);

  return {
    loading,
    error,
    success,
    execute,
    clearMessages,
    showSuccess,
    showError,
    setLoading
  };
};

/**
 * Custom hook for protected routes
 * Automatically redirects to login if not authenticated
 */
export const useProtectedRoute = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
    } else {
      setIsChecking(false);
    }
  }, [navigate]);

  return { isChecking };
};
